/**
 * WebSocket Chat Service
 *
 * Manages a single long-lived WebSocket connection for real-time chat
 * streaming. Tracks ``lastEventId`` per stream for durable delivery:
 * on reconnect the client re-subscribes with the last seen ID so the
 * server replays missed events from Redis Streams.
 */

export class WebSocketChatService {
  /**
   * @param {string} [defaultPath='/ws/admin/chat/'] Default WS route this
   *   instance connects to. Allows distinct chat flavors (cohort, omics)
   *   to each own a dedicated socket without sharing connection state.
   */
  constructor(defaultPath = '/ws/admin/chat/') {
    this.ws = null;
    this.messageHandlers = new Set();
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.currentToken = null;
    this.onReconnectCallbacks = new Set();
    this._wsPath = defaultPath;

    // Stream tracking: streamKey -> { lastEventId, conversationId }
    this._activeStreams = new Map();
    // Reverse index: conversationId -> streamKey
    this._conversationStreams = new Map();
    this._streamCursorStoragePrefix = 'chat:streamCursor:';
  }

  _streamCursorStorageKey(conversationId) {
    return `${this._streamCursorStoragePrefix}${conversationId}`;
  }

  _persistStreamCursor(conversationId, { streamKey, lastEventId }) {
    if (!conversationId || !lastEventId) return;
    try {
      sessionStorage.setItem(
        this._streamCursorStorageKey(conversationId),
        JSON.stringify({ streamKey: streamKey || null, lastEventId }),
      );
    } catch (_) {
      // sessionStorage may be unavailable in some environments
    }
  }

  _clearPersistedStreamCursor(conversationId) {
    if (!conversationId) return;
    try {
      sessionStorage.removeItem(this._streamCursorStorageKey(conversationId));
    } catch (_) {
      // noop
    }
  }

  getPersistedStreamCursor(conversationId) {
    if (!conversationId) return null;
    try {
      const raw = sessionStorage.getItem(this._streamCursorStorageKey(conversationId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.lastEventId) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  persistStreamCursor(conversationId, { streamKey, lastEventId }) {
    this._persistStreamCursor(conversationId, { streamKey, lastEventId });
  }

  /**
   * Connect to WebSocket server (single long-lived connection).
   * @param {string} token Auth0 access token
   * @param {string} [wsPath='/ws/admin/chat/'] WebSocket route path
   */
  async connect(token, wsPath = this._wsPath) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this.isConnecting) {
      return Promise.resolve();
    }

    this.isConnecting = true;
    this.currentToken = token;
    this._wsPath = wsPath;

    return new Promise((resolve, reject) => {
      try {
        const apiUrl = process.env.REACT_APP_API_SERVER_URL || 'http://localhost:8000';
        const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = apiUrl.replace(/^https?:\/\//, '');
        const wsUrl = `${wsProtocol}://${wsHost}${wsPath}`;

        this.ws = new WebSocket(wsUrl);
        let connectionEstablished = false;

        this.ws.onopen = () => {
          this.ws.send(JSON.stringify({ type: 'auth', token }));
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'auth_success') {
              connectionEstablished = true;
              const wasReconnecting = this.reconnectAttempts > 0;
              this.isConnecting = false;
              this.reconnectAttempts = 0;

              this.ws.onmessage = this._createMessageHandler();

              if (wasReconnecting) {
                this._resubscribeActiveStreams();
                this.onReconnectCallbacks.forEach(cb => {
                  try { cb(); } catch (_) { /* noop */ }
                });
              }

              resolve();
              return;
            }
            if (data.type === 'error') {
              this.isConnecting = false;
              reject(new Error(data.message || 'Authentication failed'));
              return;
            }
          } catch (err) {
            console.error('Error parsing auth response:', err);
          }
        };

        this.ws.onerror = () => {
          this.isConnecting = false;
        };

        this.ws.onclose = (event) => {
          this.isConnecting = false;
          this.ws = null;

          if (!connectionEstablished) {
            reject(new Error(`WebSocket connection failed (code: ${event.code})`));
            return;
          }

          if (event.code === 1000) {
            return;
          }

          if (this.currentToken && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
            setTimeout(() => {
              if (this.currentToken) {
                this.connect(this.currentToken).catch(() => {});
              }
            }, delay);
          }
        };
      } catch (err) {
        this.isConnecting = false;
        reject(err);
      }
    });
  }

  _createMessageHandler() {
    return (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'ping') {
          this.ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
          return;
        }

        // Track last event ID per stream for catchup on reconnect
        if (data.event_id && data._stream_key) {
          this._updateLastEventId(data._stream_key, data.event_id);
        } else if (data.event_id) {
          for (const [, info] of this._activeStreams) {
            info.lastEventId = data.event_id;
          }
        }

        // Register new streams with conversation association
        if (data.type === 'stream_started' && data.stream_key) {
          const convId = data.conversation_id || null;
          this._activeStreams.set(data.stream_key, {
            lastEventId: '0-0',
            streamKey: data.stream_key,
            conversationId: convId,
          });
          if (convId) {
            this._conversationStreams.set(convId, data.stream_key);
            this._persistStreamCursor(convId, {
              streamKey: data.stream_key,
              lastEventId: '0-0',
            });
          }
        }

        // Clean up finished streams — keep persisted cursor so omics tabs can
        // re-subscribe to the event tail after reconnect or on a second browser.
        if (data.type === 'done' || data.type === 'error') {
          const streamKey = data._stream_key;
          if (streamKey && this._activeStreams.has(streamKey)) {
            const info = this._activeStreams.get(streamKey);
            if (info.conversationId && data.event_id) {
              this._persistStreamCursor(info.conversationId, {
                streamKey,
                lastEventId: data.event_id,
              });
            }
            if (info.conversationId) {
              this._conversationStreams.delete(info.conversationId);
            }
            this._activeStreams.delete(streamKey);
          } else if (data.conversation_id && data.event_id) {
            this._persistStreamCursor(data.conversation_id, {
              streamKey: streamKey || null,
              lastEventId: data.event_id,
            });
          } else if (streamKey) {
            console.warn('Stream cleanup: unknown stream key', streamKey);
          } else if (data.type === 'error' && !data.conversation_id) {
            console.warn('Stream cleanup: missing _stream_key in done/error event');
          }
        }

        this.messageHandlers.forEach(handler => {
          try { handler(data); } catch (_) { /* noop */ }
        });
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };
  }

  _updateLastEventId(streamKey, eventId) {
    const info = this._activeStreams.get(streamKey);
    if (info) {
      info.lastEventId = eventId;
      if (info.conversationId) {
        this._persistStreamCursor(info.conversationId, {
          streamKey,
          lastEventId: eventId,
        });
      }
    }
  }

  /**
   * Re-subscribe to all active streams after a reconnect.
   * The server will replay missed events from the last known ID.
   */
  _resubscribeActiveStreams() {
    for (const [streamKey, info] of this._activeStreams) {
      this.subscribe(info.conversationId, null, info.lastEventId, streamKey);
    }
  }

  /**
   * Subscribe to an existing stream (for catchup / reconnect)
   */
  subscribe(conversationId, sequenceId, lastEventId = '0-0', streamKey = null, omicsCoords = null) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    const payload = {
      type: 'subscribe',
      conversation_id: conversationId,
      sequence_id: sequenceId,
      last_event_id: lastEventId,
    };
    if (streamKey) {
      payload.stream_key = streamKey;
    }
    if (omicsCoords?.nodeId) {
      payload.node_id = omicsCoords.nodeId;
    }
    if (omicsCoords?.chatId) {
      payload.chat_id = omicsCoords.chatId;
    }
    this.ws.send(JSON.stringify(payload));
  }

  async ensureConnected() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this.isConnecting) {
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            clearInterval(checkInterval);
            resolve();
          } else if (!this.isConnecting) {
            clearInterval(checkInterval);
            reject(new Error('Connection attempt failed'));
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Connection timeout'));
        }, 10000);
      });
    }

    if (!this.currentToken) {
      throw new Error('No authentication token available for connection');
    }

    this.reconnectAttempts = 0;
    return this.connect(this.currentToken, this._wsPath);
  }

  async sendMessage(payload) {
    await this.ensureConnected();
    this.ws.send(JSON.stringify(payload));
  }

  async cancelStream(conversationId) {
    await this.ensureConnected();
    const activeStream = this.getActiveStream(conversationId);
    const payload = {
      type: 'cancel',
      conversation_id: conversationId,
    };
    if (activeStream?.streamKey) {
      payload.stream_key = activeStream.streamKey;
    }
    this.ws.send(JSON.stringify(payload));
  }

  async cancelTool(conversationId, { toolName, sessionId, jobId, toolUseId } = {}) {
    await this.ensureConnected();
    const payload = {
      type: 'cancel_tool',
      conversation_id: conversationId,
      tool_name: toolName,
    };
    if (sessionId) payload.session_id = sessionId;
    if (jobId) payload.job_id = jobId;
    if (toolUseId) payload.tool_use_id = toolUseId;
    this.ws.send(JSON.stringify(payload));
  }

  /**
   * Drop local stream tracking for a conversation without closing the socket.
   * Used after Stop or before starting a fresh turn so stale cancel acks are ignored.
   */
  clearConversationStream(conversationId) {
    const streamKey = this._conversationStreams.get(conversationId);
    if (streamKey) {
      this._activeStreams.delete(streamKey);
    }
    this._conversationStreams.delete(conversationId);
    this._clearPersistedStreamCursor(conversationId);
  }

  onMessage(handler) {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onReconnect(callback) {
    this.onReconnectCallbacks.add(callback);
    return () => this.onReconnectCallbacks.delete(callback);
  }

  /**
   * Return the active stream info for a conversation, or null.
   */
  getActiveStream(conversationId) {
    const streamKey = this._conversationStreams.get(conversationId);
    if (!streamKey) return null;
    return this._activeStreams.get(streamKey) || null;
  }

  hasActiveStream(conversationId) {
    return this._conversationStreams.has(conversationId);
  }

  disconnect(clearToken = true) {
    if (this.isConnecting) {
      return;
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.messageHandlers.clear();
    this.onReconnectCallbacks.clear();
    this._activeStreams.clear();
    this._conversationStreams.clear();
    this.reconnectAttempts = 0;

    if (clearToken) {
      this.currentToken = null;
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Create a one-time WebSocket connection for a single request.
   * @deprecated Use connect() + sendMessage() for long-lived connections.
   * Kept for backward compatibility during migration.
   */
  async connectForRequest(token, messageHandler) {
    return new Promise((resolve, reject) => {
      const apiUrl = process.env.REACT_APP_API_SERVER_URL || 'http://localhost:8000';
      const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
      const wsHost = apiUrl.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProtocol}://${wsHost}/ws/admin/chat/`;

      const ws = new WebSocket(wsUrl);
      let connectionEstablished = false;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'auth', token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'auth_success') {
            connectionEstablished = true;

            ws.onmessage = (evt) => {
              try {
                const msg = JSON.parse(evt.data);
                if (msg.type === 'ping') {
                  ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
                  return;
                }
                messageHandler(msg);
              } catch (err) {
                console.error('Error parsing message:', err);
              }
            };

            resolve({
              send: (payload) => ws.send(JSON.stringify(payload)),
              close: () => {
                if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                  ws.onclose = null;
                  ws.close(1000, 'Request complete');
                }
              },
            });
            return;
          }

          if (data.type === 'error') {
            reject(new Error(data.message || 'Authentication failed'));
            return;
          }

          if (data.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
            return;
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.onerror = () => {};

      ws.onclose = (event) => {
        if (!connectionEstablished) {
          reject(new Error(`Connection failed (code: ${event.code})`));
        }
      };
    });
  }
}

// Export singleton instance
export const wsChat = new WebSocketChatService();
